import i18n from '@/i18n'
import { storeS } from '@/store'
import {
  mutateTypeTicket,
  mutateStatusTicket,
  mutatePriority,
  mutateProblemEnv
} from '@/usabilityScripts/globalMutate.js'

/**
 * Перекладання назв фільтрів
 */
export function translateFilterName(nameFilter) {
  const filterNameMap = {
    'types': 'ticket_type',
    'statuses': 'Status',
    'decidedStatuses': 'decidedStatus',
    'priority': 'priority',
    'workerId': 'Employee',
    'createWorkerId': 'Employee',
    'inJobWorkerId': 'inJobWorker',
    'inJobWorkerIds': 'inJobWorker',
    'workerIds': 'added',
    'assignedWorkerIds': 'appointed',
    'status': 'Status',
    'statusArchive': 'archive_status',
    'taskName': 'task_type',
    'providerType': 'provider_type',
    'provider': 'Provider',
    'problemEnvironments': 'problem_area',
    'hashtags': 'tags',
    'tags': 'tags',
    'groupId': 'Group',
    'actionCall': 'Action',
    'finedWorkerId': 'finedWorker',
    'productsCategoryId': 'category',
    'nomenclatureName': 'nomenclature',
    'workerStatus': 'workerStatus',
    'workerJob': 'position',
    'countryFilter': 'Country',
    'userFilter': 'User',
    'update': 'Update',
    'sent': 'sent_status',
    'hideMonitoringByWorker': 'hideAssigned',
    'category': 'Category',
    'team': 'Team',
    'teamlead': 'TeamLead',
    'confStatus': 'configuration_status',
    'problemEnvironment': 'problem_area'
  }

  return i18n.global.t(filterNameMap[nameFilter] || nameFilter)
}

/**
 * Перекладання значень різних типів фільтрів
 * Динамічні дані беруться зі store, фіксовані значення з globalMutate або вбудованих маппінгів
 */

export function translateFilterValue(filterName, value) {
  // Обробка масивів
  if (Array.isArray(value)) {
    if (value.length === 0) return i18n.global.t('all')
    if (value.length === 1) {
      return translateSingleValue(filterName, value[0])
    }
    return value.map(v => translateSingleValue(filterName, v)).join(', ')
  }

  return translateSingleValue(filterName, value)
}

function translateSingleValue(filterName, value) {
  // Порожнє значення = "Всі"
  if (!value || value === '') return i18n.global.t('all')

  // DEBUG: логування для діагностики
  // console.log(`[filterTranslations] filterName: ${filterName}, value:`, value, 'storeS.tags:', storeS.tags)

  // Типи тікетів - використовуємо функцію з globalMutate
  if (filterName === 'types') {
    return mutateTypeTicket(value) || value
  }

  // Статуси тікетів - використовуємо функцію з globalMutate
  if (filterName === 'statuses' || filterName === 'status' || filterName === 'decidedStatuses') {
    const result = mutateStatusTicket(value)
    return result && result.name ? result.name : value
  }

  // Пріоритет - використовуємо функцію з globalMutate
  if (filterName === 'priority') {
    return mutatePriority(value) || value
  }

  // Середовище проблеми - використовуємо функцію з globalMutate
  if (filterName === 'problemEnvironment' || filterName === 'problemEnvironments') {
    return mutateProblemEnv(value) || value
  }

  // Теги - динамічно зі store
  // Для тікетів: значення може бути JSON строка ID [] або масив
  // Для інших: значення - ID з storeS.tags (tagName) або storeS.mainTags
  if (filterName === 'tags' || filterName === 'hashtags') {
    // Спробуємо спарсити JSON (для тікетів передається JSON строка)
    let tagIds = value
    try {
      if (typeof value === 'string') {
        tagIds = JSON.parse(value)
      }
    } catch (e) {
      // Якщо не JSON, значить це просто ID
      tagIds = Array.isArray(value) ? value : [value]
    }

    // Переконаємось що це масив
    if (!Array.isArray(tagIds)) {
      tagIds = [tagIds]
    }

    // Шукаємо імена в storeS.tags (для всіх типів тегів)
    const tagNames = tagIds.map(id => {
      // Спробуємо знайти в storeS.tags
      if (storeS.tags) {
        const tag = storeS.tags.find(t => t.tagId == id)
        if (tag) return tag.tagName
      }
      // Спробуємо знайти в storeS.tagsFaq
      if (storeS.tagsFaq) {
        const tag = storeS.tagsFaq.find(t => t.tagId == id)
        if (tag) return tag.tagName
      }
      // Спробуємо знайти в storeS.tagsLessons
      if (storeS.tagsLessons) {
        const tag = storeS.tagsLessons.find(t => t.tagId == id)
        if (tag) return tag.tagName
      }
      // Якщо не знайдено - повернути ID як є
      return `#${id}`
    })

    return tagNames.join(', ')
  }

  // Працівники за ID - динамічно зі store (id → name)
  // Для фільтрів: workerId, createWorkerId, inJobWorkerId, finedWorkerId, teamlead
  if (['workerId', 'createWorkerId', 'inJobWorkerId', 'finedWorkerId', 'teamlead'].includes(filterName)) {
    if (!storeS.workers) return value
    const worker = Object.values(storeS.workers).find(w => w.workerId == value)
    return worker ? worker.workerName : value
  }

  // Масиви ID працівників - динамічно зі store (id[] → name[])
  // Для фільтрів: workerIds, inJobWorkerIds, assignedWorkerIds
  if (['workerIds', 'inJobWorkerIds', 'assignedWorkerIds'].includes(filterName)) {
    if (!storeS.workers) return value
    const workerIds = Array.isArray(value) ? value : [value]
    return workerIds.map(id => {
      const worker = Object.values(storeS.workers).find(w => w.workerId == id)
      return worker ? worker.workerName : id
    }).join(', ')
  }

  // За замовчуванням: повернути як є (для дат, пошукових термінів тощо)
  return value
}
